import { ChevronRightIcon } from "@heroicons/react/solid";
import { find, get, isEmpty, orderBy } from "lodash";
import moment from "moment";
import Tooltip from "rc-tooltip";
import React, { useCallback, useEffect, useMemo } from "react";
import { RouteComponentProps } from "react-router-dom";
import {
  Bar,
  Cell,
  ComposedChart,
  Line,
  ResponsiveContainer,
  Tooltip as ChartTooltip
} from "recharts";
import AlertMessage from "../../../../components/AlertMessage";
import CenteredLoading from "../../../../components/CenteredLoading";
import DonutChart from "../../../../components/charts/DonutChart";
import HistoricalDataChartTooltipWithCurrency from "../../../../components/charts/HistoricalDataChartTooltipWithCurrency";
import DataPeriodSelector from "../../../../components/DataPeriodSelector";
import ExternalLink from "../../../../components/ExternalLink";
import InfoIcon from "../../../../components/icons/InfoIcon";
import NavLink from "../../../../components/NavLink";
import PercentageChange, {
  calculatePercentChange
} from "../../../../components/PercentageChange";
import { useHotspots } from "../../../../context/hotspots";
import { HotspotReward } from "../../../../context/hotspots/HotspotsProvider";
import { useWorkspaces } from "../../../../context/workspaces/WorkspacesProvider";
import { Entity } from "../../../../utils/constants";
import {
  CURRENT_CALENDAR_MONTH_DISPLAY,
  PREVIOUS_CALENDAR_MONTH_DISPLAY
} from "../../../../utils/dates";
import useDataPeriod from "../../../../utils/useDataPeriod";
import { useHntMarket } from "../../../../utils/useHntMarket";
import StatisticsCardWithRelativePerformance from "../../common/StatisticsCardWithRelativePerformance";
import { renderDate } from "../HotspotInfoTab";
import WitnessedTransmitScalesDonutChart from "./WitnessedTransmitScalesDonutChart";

const HotspotRewardsTab: React.FC<RouteComponentProps<{ id: string }>> = (
  props
) => {
  const {
    currentHotspot,
    hotspotRewardsSummary,
    hotspotRewardsHistory,
    hotspotRewardsByTypeMap,
    getRewardsHistoryChartData,
    isNotFetchedInLastMinute,
    fetchRewardsHistory,
    fetchRewardSummariesByHotspots,
    lastFetched,
    fetchRewardsByTypeByHotspots,
    fetchHotspotWitnessData,
    fetchWitnessesCountsByHotspots
  } = useHotspots();
  const { currentWorkspace } = useWorkspaces();
  const { displayInCurrency } = useHntMarket();
  const dataPeriod = useDataPeriod((state) => state.dataPeriod);

  const hotspotRewardsByType = useMemo(
    () => hotspotRewardsByTypeMap[props.match.params.id],
    [hotspotRewardsByTypeMap, props.match.params.id]
  );

  const rewardsByTypeChartData = useMemo(() => {
    if (!hotspotRewardsByType) return [];

    return orderBy(hotspotRewardsByType.rewards, "amount", "desc").map(
      ({ type, amount, percentage }) => ({
        key: type,
        name: get(rewardTypes, [type, "name"], type.replace("_", " ")),
        value: amount,
        displayValues: [
          displayInCurrency(amount, {
            needsDivision: true,
            withHntSuffix: true
          })
        ],
        percentage: percentage * 100,
        color: get(rewardTypes, [type, "color"])
      })
    );
  }, [displayInCurrency, hotspotRewardsByType]);

  const getRewardsValue = useCallback(
    (rewardsSummaryField: string): number =>
      currentHotspot
        ? get(hotspotRewardsSummary, [currentHotspot?.id, rewardsSummaryField])
        : 0,
    [currentHotspot, hotspotRewardsSummary]
  );

  const rewardsByTypeAlertMessage = useMemo(() => {
    const witnessesRewards = find(
      hotspotRewardsByType?.rewards || ([] as HotspotReward[]),
      {
        type: "poc_witnesses"
      }
    ) as HotspotReward | undefined;

    const beaconingRewards = find(
      hotspotRewardsByType?.rewards || ([] as HotspotReward[]),
      {
        type: "poc_challengees"
      }
    ) as HotspotReward | undefined;

    if (!witnessesRewards?.percentage || !beaconingRewards?.percentage)
      return undefined;

    return (
      <>
        <div>
          <span className="font-semibold">
            {(
              (witnessesRewards.percentage + beaconingRewards.percentage) *
              100
            ).toLocaleString(undefined, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2
            })}
            %
          </span>{" "}
          of the rewards are exposed to transmit scale reductions.{" "}
          <span className="font-semibold">
            {(witnessesRewards.percentage * 100).toLocaleString(undefined, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2
            })}
            %
          </span>{" "}
          of the rewards come from witnessing other hotspots. Those rewards are
          reduced based on the transmit scales of the transmitting hotspots.
        </div>
        <button
          type="button"
          onClick={() => {
            props.history.push(
              `/hotspots/${props.match.params.id}/reward-scaling`
            );
          }}
          className="mt-4 flex items-center px-2.5 py-1.5 border border-transparent text-xs font-medium rounded shadow-sm text-white bg-hotspotty-400 hover:bg-hotspotty-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-hotspotty-400"
        >
          <span>Check transmit scale</span>
          <ChevronRightIcon className="h-5 w-5 ml-2" />
        </button>
      </>
    );
  }, [hotspotRewardsByType?.rewards, props.history, props.match.params.id]);

  const daysOfRewardsHistoryAvailable = useMemo(
    () =>
      currentHotspot?.id
        ? get(hotspotRewardsHistory, currentHotspot.id, []).length
        : 0,
    [currentHotspot?.id, hotspotRewardsHistory]
  );

  const rewardsHistoryChartData = useMemo(
    () => getRewardsHistoryChartData(currentHotspot?.id),
    [currentHotspot?.id, getRewardsHistoryChartData]
  );

  useEffect(() => {
    if (!currentHotspot) return;

    const now = Date.now().valueOf();

    const witnessDataFetchId = `hotspot-witness-data-${currentHotspot?.id}`;
    if (isNotFetchedInLastMinute(witnessDataFetchId)) {
      fetchHotspotWitnessData(currentHotspot.id);
      lastFetched.current[witnessDataFetchId] = now;
    }

    const witnessesCountsFetchId = `hotspot-witnesses-counts-${currentHotspot.id}`;
    if (isNotFetchedInLastMinute(witnessesCountsFetchId)) {
      fetchWitnessesCountsByHotspots([currentHotspot.id]);
      lastFetched.current[witnessesCountsFetchId] = now;
    }

    fetchRewardsHistory([currentHotspot.id], {
      historyRewardsWithoutToday: false,
      singleHotspot: true
    });

    const rewardsSummaryFetchId = `hotspot-rewards-summary-${currentHotspot.id}`;
    if (isNotFetchedInLastMinute(rewardsSummaryFetchId)) {
      fetchRewardSummariesByHotspots([currentHotspot.id]);
      lastFetched.current[rewardsSummaryFetchId] = now;
    }

    const rewardsByTypeFetchId = `hotspot-rewards-by-type-${currentHotspot.id}`;
    if (
      isNotFetchedInLastMinute(rewardsByTypeFetchId) &&
      currentWorkspace?.permissions["rewards-by-type"]
    ) {
      fetchRewardsByTypeByHotspots([currentHotspot.id]);
      lastFetched.current[rewardsByTypeFetchId] = now;
    }
  }, [
    currentHotspot,
    currentWorkspace?.permissions,
    fetchHotspotWitnessData,
    fetchRewardSummariesByHotspots,
    fetchRewardsByTypeByHotspots,
    fetchRewardsHistory,
    fetchWitnessesCountsByHotspots,
    isNotFetchedInLastMinute,
    lastFetched
  ]);

  if (!currentHotspot) return null;

  if (!hotspotRewardsSummary[currentHotspot.id]) {
    return (
      <div className="flex flex-col mt-6 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col sm:flex-row w-full items-center">
          <div className="flex w-full sm:w-4/12 h-36 animate-pulse bg-gray-400 rounded-lg" />
          <div className="flex w-full sm:w-4/12 my-5 sm:my-0 sm:ml-5 h-36 animate-pulse bg-gray-400 rounded-lg" />
          <div className="flex w-full sm:w-4/12 sm:ml-5 h-36 animate-pulse bg-gray-400 rounded-lg" />
        </div>
        <div className="flex w-full flex-col sm:flex-row mt-5">
          <div className="flex w-full sm:w-8/12 h-80 sm:mr-2 animate-pulse bg-gray-400 rounded-lg" />
          <div className="flex w-full sm:w-4/12 sm:mt-0 mt-5 sm:pl-3">
            <div className="flex w-full h-80 animate-pulse bg-gray-400 rounded-lg" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mt-6 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
      <dl className="grid grid-cols-1 gap-5 sm:grid-cols-3">
        <StatisticsCardWithRelativePerformance
          id={currentHotspot.id}
          entity={Entity.hotspot}
          title="Past 24 hours"
          metricIteratee="rewardsSummary.day"
        >
          {displayInCurrency(getRewardsValue("day"), {
            needsDivision: true
          })}
          <Tooltip overlay="Percentage change of the rewards for the last 24h compared to the daily rewards averaged over the past week">
            <PercentageChange
              percentageChange={calculatePercentChange(
                getRewardsValue("day"),
                getRewardsValue("dayWeekAverage")
              )}
            />
          </Tooltip>
        </StatisticsCardWithRelativePerformance>

        <StatisticsCardWithRelativePerformance
          id={currentHotspot.id}
          entity={Entity.hotspot}
          title="24h avg past week"
          metricIteratee="rewardsSummary.dayWeekAverage"
        >
          {displayInCurrency(getRewardsValue("dayWeekAverage"), {
            needsDivision: true
          })}
          <Tooltip overlay="Percentage change of the daily rewards' weekly average over the previous week's daily average">
            <PercentageChange
              percentageChange={calculatePercentChange(
                getRewardsValue("dayWeekAverage"),
                getRewardsValue("dayPreviousWeekAverage")
              )}
            />
          </Tooltip>
        </StatisticsCardWithRelativePerformance>

        <StatisticsCardWithRelativePerformance
          id={currentHotspot.id}
          entity={Entity.hotspot}
          title="Past 30 days"
          metricIteratee="rewardsSummary.month"
        >
          {displayInCurrency(getRewardsValue("month"), {
            needsDivision: true
          })}
          <Tooltip overlay="Percentage change rewards of the past month over the previous month">
            <PercentageChange
              percentageChange={calculatePercentChange(
                getRewardsValue("month"),
                getRewardsValue("previousMonth")
              )}
            />
          </Tooltip>
        </StatisticsCardWithRelativePerformance>

        <div className="bg-white dark:bg-gray-800 shadow rounded-lg sm:col-span-2">
          <div className="px-4 py-5 sm:p-6">
            <dt className="text-sm font-medium text-gray-500 dark:text-gray-300 flex items-center">
              <div className="flex-1 flex items-center">
                <span className="truncate">Rewards by day</span>

                {!!currentHotspot.last_assertion &&
                  moment()
                    .utc()
                    .subtract(
                      dataPeriod.days || daysOfRewardsHistoryAvailable,
                      "days"
                    )
                    .isBefore(
                      moment.unix(currentHotspot.last_assertion).utc()
                    ) && (
                    <Tooltip
                      overlay={`Rewards before the most recent assertion ${moment
                        .unix(currentHotspot.last_assertion)
                        .utc()
                        .fromNow()} are greyed out`}
                    >
                      <InfoIcon className="ml-1 inline-flex h-4 w-4" />
                    </Tooltip>
                  )}
              </div>
              <DataPeriodSelector className="min-w-max" />
            </dt>

            <dd className="mt-1">
              <div className="w-full h-48">
                {hotspotRewardsHistory[currentHotspot.id] && (
                  <ResponsiveContainer>
                    <ComposedChart data={rewardsHistoryChartData}>
                      <Bar dataKey="total" isAnimationActive={false}>
                        {rewardsHistoryChartData.map((entry, index) => (
                          <Cell
                            key={`cell-${index}`}
                            fill={
                              currentHotspot.last_assertion &&
                              moment(entry.timestamp)
                                .utc()
                                .isBefore(
                                  moment
                                    .unix(currentHotspot.last_assertion)
                                    .utc()
                                )
                                ? "rgba(170, 108, 235, 0.4)"
                                : "#a96ceb"
                            }
                          />
                        ))}
                      </Bar>
                      <Line
                        type="monotone"
                        dataKey="sma7"
                        isAnimationActive={false}
                        stroke="#fcc945"
                        strokeWidth={2.5}
                        dot={false}
                      />
                      <ChartTooltip
                        content={HistoricalDataChartTooltipWithCurrency}
                        cursor={false}
                        isAnimationActive={false}
                      />
                    </ComposedChart>
                  </ResponsiveContainer>
                )}
              </div>
            </dd>
          </div>
        </div>

        <div className="relative bg-white dark:bg-gray-800 overflow-hidden shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <dt className="text-sm font-medium text-gray-500 dark:text-gray-300 truncate flex items-center">
              {CURRENT_CALENDAR_MONTH_DISPLAY}
            </dt>

            <dd className="mt-1 text-3xl font-semibold text-gray-900 dark:text-white pb-6">
              {displayInCurrency(getRewardsValue("currentCalendarMonth"), {
                needsDivision: true
              })}
            </dd>

            <dt className="text-sm font-medium text-gray-500 dark:text-gray-300 truncate flex items-center">
              {PREVIOUS_CALENDAR_MONTH_DISPLAY}
            </dt>

            <dd className="mt-1 text-3xl font-semibold text-gray-900 dark:text-white pb-6">
              {displayInCurrency(getRewardsValue("previousCalendarMonth"), {
                needsDivision: true
              })}
            </dd>

            <dt className="text-sm font-medium text-gray-500 dark:text-gray-300 truncate flex items-center">
              Lifetime
              <Tooltip
                overlay={`Since ${renderDate(currentHotspot.timestamp_added)}`}
              >
                <InfoIcon className="inline-flex h-4 w-4 ml-1" />
              </Tooltip>
            </dt>

            <dd className="mt-1 text-3xl font-semibold text-gray-900 dark:text-white">
              {displayInCurrency(getRewardsValue("lifetime"), {
                needsDivision: true
              })}
            </dd>
          </div>
        </div>
      </dl>

      {!!getRewardsValue("week") && (
        <dl className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2 pb-6">
          <div>
            <dt className="text-sm font-medium text-gray-500 dark:text-gray-300">
              <ExternalLink
                className="inline-flex"
                iconStyle={{ width: "1rem", height: "1rem" }}
                text="Rewards by type"
                url="https://docs.helium.com/blockchain/mining"
              />
              {!isEmpty(rewardsByTypeChartData) && (
                <Tooltip
                  overlay={`This shows the composition of rewards starting from ${moment
                    .unix(hotspotRewardsByType.first_reward_timestamp)
                    .utc()
                    .fromNow()} until ${moment
                    .unix(hotspotRewardsByType.last_reward_timestamp)
                    .utc()
                    .fromNow()}`}
                >
                  <InfoIcon className="ml-1 inline-flex h-4 w-4" />
                </Tooltip>
              )}
            </dt>

            <dd className="mt-4">
              {!currentWorkspace?.permissions["rewards-by-type"] ? (
                <span className="text-sm">
                  View a breakdown of the rewards by type for this hotspot by
                  upgrading to the <b>Pro</b> or <b>Business</b> plan.{" "}
                  <NavLink
                    to="/pricing"
                    className="font-bold text-hotspotty-300 hover:underline"
                  >
                    Click here
                  </NavLink>{" "}
                  to upgrade now.
                </span>
              ) : !isEmpty(hotspotRewardsByTypeMap) ? (
                hotspotRewardsByType &&
                isEmpty(rewardsByTypeChartData) &&
                currentHotspot.last_assertion ? (
                  <span className="text-sm text-gray-600 dark:text-gray-300">
                    {`No rewards yet since the last assertion ${moment
                      .unix(currentHotspot.last_assertion)
                      .utc()
                      .fromNow()}`}
                  </span>
                ) : (
                  <>
                    <DonutChart
                      className="mx-auto"
                      data={rewardsByTypeChartData}
                      sizeInPx={240}
                    />
                    {rewardsByTypeAlertMessage && (
                      <AlertMessage
                        className="mt-4"
                        type="info"
                        title="Transmit scale reductions"
                        message={rewardsByTypeAlertMessage}
                        collapsible
                      />
                    )}
                  </>
                )
              ) : (
                <CenteredLoading className="h-60" />
              )}
            </dd>
          </div>

          <WitnessedTransmitScalesDonutChart hotspot={currentHotspot} />
        </dl>
      )}
    </div>
  );
};

export default HotspotRewardsTab;

//
// Utils
//

export const rewardTypes = {
  consensus: { name: "Consensus", color: "#FF6666" },
  poc_challengers: { name: "Submitting challenges", color: "#BE73FF" },
  poc_challengees: { name: "Being witnessed", color: "#38A2FF" },
  poc_witnesses: { name: "Witnessing", color: "#FFC769" },
  data_credits: { name: "Data transfer", color: "#9AE8C9" }
};
